import React, { FC } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';
import { getSprintGroups } from '../../utils/getSprintGroups';
import { SprintMobile } from './SprintMobile';

type ResultsTableProps = {
    values: string[][];
    title?: string;
};

export const SprintTable: FC<ResultsTableProps> = ({ values, title }) => {
    const [headers, ...rows] = values;

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');

    // 🎯 NOVÁ LOGIKA: Reaguje na Q, QG, QB, Q5
    const validQValues = ['Q', 'QG', 'QB', 'Q5'];
    const isQualified = (value?: string) => value ? validQValues.includes(value.toUpperCase().trim()) : false;
    
    const isWinner = (value?: string) => value?.toLowerCase() === 'winner';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const mobileData = getSprintGroups(values);

    // =========================================================
    // 📱 MOBILE VIEW
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, mb: 2 }}>
                {title && (
                    <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
                        {/* 🏷️ Title */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                backgroundColor: 'grey.900',
                                color: 'common.white',
                            }}
                        >
                            <Typography fontWeight={600}>{title}</Typography>
                        </Box>
                    </Paper>
                )}

                {mobileData.map((group, index) => (
                    <Paper key={index} sx={{ p: 2, borderRadius: 3 }}>
                        <SprintMobile
                            riders={group}
                            heat1TimeIndex={6}
                            heat2TimeIndex={8}
                            deciderTimeIndex={10}
                            countryColIndex={4}
                            getFlagUrl={getFlagUrl}
                        />
                    </Paper>
                ))}
            </Box>
        );
    }

    return (
        <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2, overflow: 'hidden', mt: 3 }}>
            {/* 🏷️ Title */}
            {title && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: 'grey.900',
                        color: 'common.white',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Typography fontWeight={600}>{title}</Typography>
                </Box>
            )}

            {/* 💻 DESKTOP */}
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <TableCell
                                    key={index}
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        backgroundColor: 'grey.900',
                                        color: 'common.white',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex} hover>
                                {headers.map((_, cellIndex) => {
                                    const cellValue = row[cellIndex];
                                    const winner = isWinner(cellValue);
                                    const qualified = cellIndex === qColIndex && isQualified(cellValue);

                                    if (qualified) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                {/* 🎯 Zde se dynamicky vypíše přesný text (např. QG) */}
                                                <span className="qualified-badge">{cellValue.toUpperCase().trim()}</span>
                                            </TableCell>
                                        );
                                    }

                                    if (cellIndex === countryColIndex && cellValue) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <img
                                                        src={getFlagUrl(cellValue)}
                                                        alt={cellValue}
                                                        className="flag-icon"
                                                    />
                                                </Box>
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                color: winner ? 'success.main' : 'text.primary',
                                                fontWeight: winner || cellIndex === 0 ? 600 : 400,
                                            }}
                                        >
                                            {cellValue ?? '—'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};